import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { getJobStatusLabel } from '../utils/jobStatus'

function AdminJobsView() {
  const { user } = useAuth()
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    failed: 0,
    processing: 0,
    byUser: {}
  })

  const fetchAllJobs = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('auth_token')
      
      const response = await fetch('/api/v1/admin/all-jobs', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied. Admin privileges required.')
        }
        throw new Error(`Failed to fetch jobs: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.success) {
        setJobs(data.data || [])
        calculateStats(data.data || [])
      } else {
        throw new Error(data.error || 'Failed to fetch jobs')
      }
    } catch (err) {
      console.error('Error fetching jobs:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const calculateStats = (jobsList) => {
    const stats = {
      total: jobsList.length,
      completed: 0,
      failed: 0,
      processing: 0,
      byUser: {}
    }

    jobsList.forEach(job => {
      // Count by status
      if (job.status === 'completed') stats.completed++
      else if (job.status === 'failed') stats.failed++
      else if (!['completed', 'failed'].includes(job.status)) stats.processing++

      // Count by user
      const userEmail = job.userEmail || job.user_email || 'Unknown'
      if (!stats.byUser[userEmail]) {
        stats.byUser[userEmail] = { total: 0, completed: 0, failed: 0, processing: 0 }
      }
      stats.byUser[userEmail].total++
      
      if (job.status === 'completed') stats.byUser[userEmail].completed++
      else if (job.status === 'failed') stats.byUser[userEmail].failed++
      else stats.byUser[userEmail].processing++
    })

    setStats(stats)
  }

  useEffect(() => {
    fetchAllJobs()
    // Refresh every 30 seconds
    const interval = setInterval(fetchAllJobs, 30000)
    return () => clearInterval(interval)
  }, [])

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } catch {
      return 'Invalid date'
    }
  }

  const formatUrl = (url) => {
    try {
      const urlObj = new URL(url)
      const host = urlObj.hostname.replace(/^www\./, '')
      const firstSegment = urlObj.pathname.split('/').filter(Boolean)[0]
      return firstSegment ? `${host}/${firstSegment}` : host
    } catch {
      return url || 'Unknown'
    }
  }

  const getStatusBadge = (status) => {
    const variants = {
      'completed': 'bg-green-100 text-green-800',
      'partial_success': 'bg-green-100 text-green-800',
      'partially_completed': 'bg-green-100 text-green-800',
      'failed': 'bg-red-100 text-red-800',
      'processing': 'bg-blue-100 text-blue-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'queued': 'bg-yellow-100 text-yellow-800'
    }
    
    if (variants[status]) {
      return variants[status]
    }
    
    if (status?.includes('executing') || status?.includes('processing') || status?.includes('step')) {
      return 'bg-blue-100 text-blue-800'
    }
    
    return 'bg-gray-100 text-gray-800'
  }

  const getStatusText = (status) => getJobStatusLabel(status)

  if (!user?.profile?.is_admin) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div className="text-red-600 text-lg mb-2">🚫</div>
        <h3 className="text-lg font-medium text-red-900 mb-1">Access Denied</h3>
        <p className="text-sm text-red-700">Admin privileges required to view this page.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <div className="animate-spin text-blue-600 text-2xl mb-2">⏳</div>
        <p className="text-muted-foreground">Loading all jobs...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-red-900 mb-1">Error Loading Jobs</h3>
        <p className="text-sm text-red-700">{error}</p>
        <button 
          onClick={fetchAllJobs}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    )
  }

  const sortedJobs = [...jobs].sort((a, b) => {
    const dateA = new Date(a.created_at || a.createdAt || 0)
    const dateB = new Date(b.created_at || b.createdAt || 0)
    return dateB - dateA
  })

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="text-sm font-medium text-muted-foreground">Total Jobs</div>
          <div className="mt-1 text-2xl font-semibold text-foreground">{stats.total}</div>
        </div>
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="text-sm font-medium text-muted-foreground">Completed</div>
          <div className="mt-1 text-2xl font-semibold text-green-600">{stats.completed}</div>
        </div>
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="text-sm font-medium text-muted-foreground">Failed</div>
          <div className="mt-1 text-2xl font-semibold text-red-600">{stats.failed}</div>
        </div>
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="text-sm font-medium text-muted-foreground">Processing</div>
          <div className="mt-1 text-2xl font-semibold text-blue-600">{stats.processing}</div>
        </div>
      </div>

      {/* User Summary */}
      <div className="bg-card rounded-lg border border-border">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Jobs by User</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-background border-b border-border">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  User
                </th>
                <th className="text-center py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Total
                </th>
                <th className="text-center py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Completed
                </th>
                <th className="text-center py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Failed
                </th>
                <th className="text-center py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Processing
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Object.entries(stats.byUser).map(([userEmail, userStats]) => (
                <tr key={userEmail} className="hover:bg-background">
                  <td className="py-3 px-4 text-sm text-foreground">{userEmail}</td>
                  <td className="py-3 px-4 text-sm text-center text-foreground">{userStats.total}</td>
                  <td className="py-3 px-4 text-sm text-center text-green-600">{userStats.completed}</td>
                  <td className="py-3 px-4 text-sm text-center text-red-600">{userStats.failed}</td>
                  <td className="py-3 px-4 text-sm text-center text-blue-600">{userStats.processing}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* All Jobs Table */}
      <div className="bg-card rounded-lg border border-border">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">All Jobs ({jobs.length})</h3>
          <button
            onClick={fetchAllJobs}
            className="text-sm text-muted-foreground hover:text-gray-800 px-3 py-1 rounded border border-border hover:bg-background"
          >
            Refresh
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-background border-b border-border">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  User
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Website
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Created
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Job ID
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedJobs.map(job => (
                <tr key={job.id} className="hover:bg-background">
                  <td className="py-3 px-4 text-sm text-foreground">
                    {job.userEmail || job.user_email || 'Unknown'}
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm font-medium text-foreground truncate max-w-xs" title={job.url}>
                      {formatUrl(job.url)}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(job.status)}`}>
                      {getStatusText(job.status)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">
                    {formatDate(job.created_at || job.createdAt)}
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground font-mono text-xs">
                    {job.id}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {jobs.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">
            No jobs found in the system.
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminJobsView